"use client";

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useStaffTimeTracking } from "@/context/StaffTimeTrackingContext";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatTime } from "@/lib/utils";
import { Loader2, Calendar, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { format, subDays } from "date-fns";

// Helper function to format hours
function formatHours(hours: number): string {
  return `${hours.toFixed(2)}h`;
}

// Helper function to format duration in hours and minutes
function formatWorkingHours(minutes: number | null): string {
  if (minutes === null) return "N/A";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

// Helper function to format pause duration
function formatPauseDuration(minutes: number): string {
  if (minutes === 0) return "0m";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return (
        <Badge className="bg-green-500 text-white">Active</Badge>
      );
    case "PAUSED":
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
          Paused
        </Badge>
      );
    case "ENDED":
      return (
        <Badge variant="outline" className="bg-gray-50 text-gray-700">
          Ended
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

export function TimeTrackingReports() {
  const { getReports, getHistory } = useStaffTimeTracking();
  const { staff } = useApp();
  const { user, isLoading: authLoading } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [reports, setReports] = useState<any[]>([]);
  const [historyRecords, setHistoryRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>(
    format(subDays(new Date(), 30), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [expandedStaff, setExpandedStaff] = useState<Set<string>>(new Set());

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params: {
        userId?: string;
        startDate?: string;
        endDate?: string;
      } = {};
      
      // Only add userId if a specific staff is selected (not "all")
      if (selectedStaffId && selectedStaffId.trim() !== "" && selectedStaffId !== "all") {
        params.userId = selectedStaffId;
      }
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      // Debug logging
      console.log("Fetching reports with params:", params);
      console.log("Selected staff ID:", selectedStaffId);
      console.log("Params object:", JSON.stringify(params));

      const data = await getReports(params);
      
      // Debug logging
      console.log("Reports data received:", data);
      console.log("Reports count:", data?.length);
      
      setReports(data || []);
    } catch (error: any) {
      console.error("Error fetching reports:", error);
      // Set empty state on error
      setReports([]);
      // Error toast is already shown by the context
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const params: {
        userId?: string;
        limit?: number;
      } = {};
      
      // Only add userId if a specific staff is selected (not "all")
      if (selectedStaffId && selectedStaffId.trim() !== "" && selectedStaffId !== "all") {
        params.userId = selectedStaffId;
      }
      if (startDate && endDate) {
        // Fetch history for the date range
        // Note: API might need date range support, for now we'll fetch all and filter client-side
        params.limit = 500; // Get more records for date range
      }

      // Debug logging
      console.log("Fetching history (reports tab) with params:", params);
      console.log("Selected staff ID:", selectedStaffId);
      console.log("Params object:", JSON.stringify(params));

      const data = await getHistory(params);
      
      // Filter by date range if dates are provided
      let filteredRecords = data.records || [];
      if (startDate && endDate && filteredRecords.length > 0) {
        filteredRecords = filteredRecords.filter((record: any) => {
          const recordDate = format(new Date(record.date), "yyyy-MM-dd");
          return recordDate >= startDate && recordDate <= endDate;
        });
      }
      
      // Debug logging
      console.log("History records (reports tab) received:", filteredRecords.length);
      console.log("Filtered records:", filteredRecords.map((r: any) => ({ id: r.id, userId: r.userId, staffName: r.staffName })));
      
      setHistoryRecords(filteredRecords);
    } catch (error: any) {
      console.error("Error fetching history:", error);
      // Set empty state on error
      setHistoryRecords([]);
      // Error toast is already shown by the context
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaffId, startDate, endDate]);

  const toggleExpand = (userId: string) => {
    setExpandedStaff((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Show access denied for non-admins
  if (!authLoading && !isAdmin) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <div>
              <h3 className="text-lg font-semibold">Access Denied</h3>
              <p className="text-sm text-muted-foreground mt-2">
                You must be an administrator to view time tracking reports.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Time Tracking Reports</CardTitle>
          <CardDescription>
            View aggregated working hours reports for staff members
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid gap-4 sm:grid-cols-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Staff Member</label>
              <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                <SelectTrigger>
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Start Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">End Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={() => {
                  fetchReports();
                  fetchHistory();
                }} 
                variant="outline" 
                className="w-full"
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Tabs for Reports and History */}
          <Tabs defaultValue="summary" className="space-y-4">
            <TabsList>
              <TabsTrigger value="summary">Summary Reports</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="space-y-4">
              {/* Reports */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No reports found for the selected period
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <Card key={report.userId} className="border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">
                          {report.staffName || "Unknown"}
                        </CardTitle>
                        <CardDescription>
                          Period: {formatDate(startDate)} - {formatDate(endDate)}
                        </CardDescription>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpand(report.userId)}
                      >
                        {expandedStaff.has(report.userId) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Days</p>
                        <p className="text-2xl font-bold">{report.totalDays}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Hours</p>
                        <p className="text-2xl font-bold">
                          {formatHours(report.totalWorkingHours)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Average Hours/Day</p>
                        <p className="text-2xl font-bold">
                          {formatHours(report.averageHoursPerDay)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Minutes</p>
                        <p className="text-2xl font-bold">{report.totalWorkingMinutes}</p>
                      </div>
                    </div>

                    {/* Detailed Records */}
                    {expandedStaff.has(report.userId) && report.records && report.records.length > 0 && (
                      <div className="mt-6 border-t pt-4">
                        <h4 className="text-sm font-semibold mb-3">Detailed Records</h4>
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Start Time</TableHead>
                                <TableHead>End Time</TableHead>
                                <TableHead>Working Hours</TableHead>
                                <TableHead>Pause Duration</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {report.records.map((record: any) => (
                                <TableRow key={record.id}>
                                  <TableCell>{formatDate(record.date)}</TableCell>
                                  <TableCell>
                                    {format(new Date(record.startTime), "HH:mm")}
                                  </TableCell>
                                  <TableCell>
                                    {record.endTime
                                      ? format(new Date(record.endTime), "HH:mm")
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {record.totalWorkingMinutes !== null
                                      ? formatHours(record.totalWorkingMinutes / 60)
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {record.totalPauseDuration > 0
                                      ? `${Math.floor(record.totalPauseDuration / 60)}h ${record.totalPauseDuration % 60}m`
                                      : "0m"}
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant={
                                        record.status === "ENDED"
                                          ? "outline"
                                          : record.status === "ACTIVE"
                                          ? "default"
                                          : "secondary"
                                      }
                                    >
                                      {record.status}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
            </TabsContent>

            <TabsContent value="history" className="space-y-4">
              {/* History Table */}
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : historyRecords.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No history records found for the selected period
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Staff Name</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Start Time</TableHead>
                        <TableHead>End Time</TableHead>
                        <TableHead>Working Hours</TableHead>
                        <TableHead>Pause Duration</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyRecords.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">
                            {record.staffName || "Unknown"}
                          </TableCell>
                          <TableCell>{formatDate(record.date)}</TableCell>
                          <TableCell>{formatTime(record.startTime)}</TableCell>
                          <TableCell>
                            {record.endTime ? formatTime(record.endTime) : "N/A"}
                          </TableCell>
                          <TableCell>
                            {formatWorkingHours(record.totalWorkingMinutes)}
                          </TableCell>
                          <TableCell>
                            {formatPauseDuration(record.totalPauseDuration)}
                          </TableCell>
                          <TableCell>{getStatusBadge(record.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

